import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

// Global store to persist state across morph operations
/** @type {Map<string, { isSubscription: boolean, sellingPlanName: string | undefined }>} */
const sellingPlanFrequencyState = new Map();

/**
 * A custom element that manages selling plan frequency selection.
 *
 * @extends Component
 */
export class SellingPlanFrequencyComponent extends Component {
  /** @type {Array<any>} */
  #sellingPlanGroups = [];
  /** @type {HTMLInputElement | null} */
  #sellingPlanInput = null;
  /** @type {any} */
  #currentVariant = null;

  connectedCallback() {
    super.connectedCallback();

    // Create and add hidden selling plan input to product form
    this.#setupSellingPlanInput();

    // Get product selling plan groups from data attribute
    this.#loadSellingPlanGroups();

    // Set up radio button change handlers
    const radioButtons = this.querySelectorAll(
      'input[type="radio"][name*="selling-plan-frequency"]'
    );
    radioButtons.forEach((radio) => {
      radio.addEventListener("change", this.#handleRadioChange.bind(this));
    });

    // Listen for quantity changes to auto-select selling plan
    document.addEventListener(
      "quantity-picker:change",
      this.#quantityChangeHandler.bind(this)
    );

    // Listen for variant updates - use closest .shopify-section as the event bubbles from variant-picker
    // We need a common ancestor of both variant-picker and this component
    const section = this.closest(".shopify-section") || document.body;
    section.addEventListener(ThemeEvents.variantUpdate, () => {
      // Re-setup the selling plan input - morph may have removed it from the DOM
      this.#resetSellingPlanInput();

      // Re-load selling plan groups on variant change (data attribute should update)
      this.#loadSellingPlanGroups();

      // Restore saved selections after morph has reset the DOM
      const sectionId = this.dataset.sectionId || "";
      const savedState = sellingPlanFrequencyState.get(sectionId);

      if (savedState) {
        // Restore the saved subscription/one-time selection
        const targetRadio = savedState.isSubscription
          ? /** @type {HTMLInputElement | null} */ (
              this.querySelector(
                'input[type="radio"][data-option-type="subscription"]'
              )
            )
          : /** @type {HTMLInputElement | null} */ (
              this.querySelector(
                'input[type="radio"][data-option-type="one-time"]'
              )
            );

        if (targetRadio) {
          targetRadio.checked = true;

          if (savedState.isSubscription) {
            // Enable selling plan input and restore selling plan
            if (this.#sellingPlanInput) {
              this.#sellingPlanInput.disabled = false;
            }
            // if (savedState.sellingPlanName) {
            //   this.#updateSellingPlan(savedState.sellingPlanName);
            // } else {
            //   this.#updateSellingPlanFromQuantity();
            // }
            this.#updateSellingPlanFromQuantity();
          } else {
            // One-time is selected - disable input
            if (this.#sellingPlanInput) {
              this.#sellingPlanInput.disabled = true;
              this.#sellingPlanInput.value = "";
            }
          }
        }
      } else {
        // No saved state - use default behavior
        this.#updateSellingPlanFromQuantity();
      }

      // Reload variant data and update prices
      this.#loadCurrentVariant();
      this.#updatePrices();
    });

    // Check if we have saved state from before a morph operation
    const sectionId = this.dataset.sectionId || "";
    const savedState = sellingPlanFrequencyState.get(sectionId);

    if (savedState) {
      // Restore the saved subscription/one-time selection
      const targetRadio = savedState.isSubscription
        ? this.querySelector(
            'input[type="radio"][data-option-type="subscription"]'
          )
        : this.querySelector(
            'input[type="radio"][data-option-type="one-time"]'
          );

      if (targetRadio instanceof HTMLInputElement) {
        targetRadio.checked = true;

        if (savedState.isSubscription) {
          // Enable selling plan input and restore selling plan
          if (this.#sellingPlanInput) {
            this.#sellingPlanInput.disabled = false;
          }
          if (savedState.sellingPlanName) {
            this.#updateSellingPlan(savedState.sellingPlanName);
          } else {
            this.#updateSellingPlanFromQuantity();
          }
        } else {
          // One-time is selected - disable input
          if (this.#sellingPlanInput) {
            this.#sellingPlanInput.disabled = true;
            this.#sellingPlanInput.value = "";
          }
        }

        // Load current variant data and update prices after restoration
        // Use setTimeout to ensure other components (like quantity-picker) are also restored
        setTimeout(() => {
          this.#loadCurrentVariant();
          this.#updatePrices();
        }, 0);

        return; // Skip default initialization
      }
    }

    // Check if subscription should be preselected
    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][data-option-type="subscription"]')
    );
    const preselectSubscription = subscriptionRadio?.checked === true;

    if (preselectSubscription) {
      // Enable the selling plan input
      if (this.#sellingPlanInput) {
        this.#sellingPlanInput.disabled = false;
      }

      // Get initial selling plan name from data attribute if available
      const initialSellingPlanName =
        subscriptionRadio?.dataset.initialSellingPlanName;
      if (initialSellingPlanName) {
        this.#updateSellingPlan(initialSellingPlanName);
      } else {
        // Fallback: find first available selling plan from first variant
        this.#selectFirstAvailableSellingPlan();
      }
    } else {
      // One-time is selected - disable input
      if (this.#sellingPlanInput) {
        this.#sellingPlanInput.disabled = true;
      }
    }

    // Always sync selling plan on initial load (will update based on quantity if subscription is selected)
    // This ensures quantity-based selling plan selection takes precedence if available
    this.#updateSellingPlanFromQuantity();

    // Load current variant data and update prices
    this.#loadCurrentVariant();
    this.#updatePrices();
  }

  /**
   * Sets up the hidden selling plan input in the product form
   */
  #setupSellingPlanInput() {
    const productForm = document.querySelector("product-form-component");

    if (!productForm) return;

    const form = productForm.querySelector("form");
    if (!form) return;

    // Check if input already exists
    const existingInput = form.querySelector('input[name="selling_plan"]');
    if (existingInput instanceof HTMLInputElement) {
      this.#sellingPlanInput = existingInput;
      return;
    }

    // Create new input
    const sellingPlanInput = document.createElement("input");
    sellingPlanInput.type = "hidden";
    sellingPlanInput.name = "selling_plan";
    form.appendChild(sellingPlanInput);
    this.#sellingPlanInput = sellingPlanInput;
  }

  /**
   * Resets and re-creates the selling plan input after morph has potentially removed it
   */
  #resetSellingPlanInput() {
    // Clear the reference - the old input may have been removed by morph
    this.#sellingPlanInput = null;

    const productForm = document.querySelector("product-form-component");
    if (!productForm) return;

    const form = productForm.querySelector("form");
    if (!form) return;

    // Check if input already exists in the new DOM
    const existingInput = form.querySelector('input[name="selling_plan"]');
    if (existingInput instanceof HTMLInputElement) {
      this.#sellingPlanInput = existingInput;
      return;
    }

    // Create new input
    const sellingPlanInput = document.createElement("input");
    sellingPlanInput.type = "hidden";
    sellingPlanInput.name = "selling_plan";
    form.appendChild(sellingPlanInput);
    this.#sellingPlanInput = sellingPlanInput;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      "quantity-picker:change",
      this.#quantityChangeHandler
    );
  }

  /**
   * @param {CustomEvent} event
   */
  #quantityChangeHandler(event) {
    // Always update selling plan when quantity changes
    this.#updateSellingPlanFromQuantity(event.detail.quantity);
    // Update prices when quantity changes
    this.#updatePrices();
  }

  /**
   * @param {Event} event
   */
  #handleRadioChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const optionType = target.dataset.optionType;
    if (!this.#sellingPlanInput) {
      this.#setupSellingPlanInput();
    }

    // Save state for persistence across morph operations
    const sectionId = this.dataset.sectionId || "";
    const isSubscription = optionType === "subscription";

    if (isSubscription) {
      if (this.#sellingPlanInput) {
        this.#sellingPlanInput.disabled = false;
      }
      // Update selling plan when switching to subscription
      this.#updateSellingPlanFromQuantity();

      // Get the selected selling plan name to save
      const subscriptionOption = /** @type {HTMLElement | null} */ (
        this.querySelector(".selling-plan-frequency__option--subscription")
      );
      const sellingPlanName =
        subscriptionOption?.dataset.selectedSellingPlanName || undefined;

      if (sectionId) {
        sellingPlanFrequencyState.set(sectionId, {
          isSubscription: true,
          sellingPlanName,
        });
      }

      // Update prices when switching to subscription
      this.#updatePrices();
    } else {
      // Save one-time selection
      if (sectionId) {
        sellingPlanFrequencyState.set(sectionId, {
          isSubscription: false,
          sellingPlanName: undefined,
        });
      }

      // Update prices when switching to one-time
      this.#updatePrices();
      // One-time purchase - disable input but keep value for potential future use
      if (this.#sellingPlanInput) {
        this.#sellingPlanInput.disabled = true;
        this.#sellingPlanInput.value = "";
      }
    }
  }

  /**
   * Checks if subscription option is currently selected
   * @returns {boolean}
   */
  #isSubscriptionSelected() {
    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][data-option-type="subscription"]')
    );
    return subscriptionRadio?.checked === true;
  }

  /**
   * Loads selling plan groups from data attribute
   */
  #loadSellingPlanGroups() {
    const groupsData = this.dataset.sellingPlanGroups;
    if (groupsData) {
      try {
        this.#sellingPlanGroups = JSON.parse(groupsData);
      } catch (e) {
        console.warn("Failed to parse selling plan groups:", e);
        this.#sellingPlanGroups = [];
      }
    } else {
      this.#sellingPlanGroups = [];
    }
  }

  /**
   * Updates selling plan based on current quantity selection
   * @param {number} [quantity] - Optional quantity override
   */
  #updateSellingPlanFromQuantity(quantity) {
    // Get current quantity from quantity picker radio buttons
    if (!quantity) {
      const quantityPicker = document.querySelector("quantity-picker");
      if (quantityPicker) {
        const selectedRadio = /** @type {HTMLInputElement | null} */ (
          quantityPicker.querySelector('input[type="radio"]:checked')
        );
        if (selectedRadio) {
          quantity =
            parseInt(selectedRadio.dataset.quantity || selectedRadio.value) ||
            1;
        }
      }

      // Fallback to quantity selector
      if (!quantity) {
        const productForm = /** @type {any} */ (
          document.querySelector("product-form-component")
        );
        const quantitySelector = productForm?.refs?.quantitySelector;
        if (quantitySelector) {
          quantity = parseInt(quantitySelector.getValue?.() || "1");
        }
      }
    }

    if (!quantity) return;

    // Find selling plan name from quantity picker option
    const quantityPicker = document.querySelector("quantity-picker");
    let sellingPlanName = null;

    if (quantityPicker) {
      const selectedRadio = /** @type {HTMLInputElement | null} */ (
        quantityPicker.querySelector(
          `input[type="radio"][data-quantity="${quantity}"]:checked`
        )
      );
      if (selectedRadio) {
        sellingPlanName = selectedRadio.dataset.sellingPlanName || null;
      }
    }

    // If no selling plan name from quantity picker, try to find one based on quantity
    if (!sellingPlanName && this.#sellingPlanGroups.length > 0) {
      // Try to find a selling plan that matches the quantity
      // This is a fallback - ideally the quantity picker option should have the selling plan name
      for (const group of this.#sellingPlanGroups) {
        for (const plan of group.selling_plans || []) {
          // You might need to adjust this logic based on how your selling plans are named
          // For now, we'll use the first available plan
          sellingPlanName = plan.name;
          break;
        }
        if (sellingPlanName) break;
      }
    }

    if (sellingPlanName) {
      this.#updateSellingPlan(sellingPlanName);
    } else {
      // Clear the selected selling plan name if no match found
      const subscriptionOption = this.querySelector(
        ".selling-plan-frequency__option--subscription"
      );
      if (subscriptionOption) {
        subscriptionOption.setAttribute("data-selected-selling-plan-name", "");
        // Hide all features
        const features = this.querySelectorAll(".selling-plan-feature");
        features.forEach((feature) => {
          feature.classList.remove("selling-plan-feature--visible");
        });
      }
      if (this.#sellingPlanInput) {
        this.#sellingPlanInput.value = "";
        // Only disable if one-time is selected
        if (!this.#isSubscriptionSelected()) {
          this.#sellingPlanInput.disabled = true;
        }
      }
    }
  }

  /**
   * Updates the selling plan in the product form
   * @param {string} sellingPlanName - The name of the selling plan to select
   */
  #updateSellingPlan(sellingPlanName) {
    if (!sellingPlanName) return;

    // Use stored selling plan groups or load them
    if (this.#sellingPlanGroups.length === 0) {
      this.#loadSellingPlanGroups();
    }

    // Find the selling plan by name
    let targetSellingPlanId = null;
    for (const group of this.#sellingPlanGroups) {
      for (const plan of group.selling_plans || []) {
        if (plan.name === sellingPlanName) {
          targetSellingPlanId = plan.id;
          break;
        }
      }
      if (targetSellingPlanId) break;
    }

    // Update the hidden selling plan input
    if (!this.#sellingPlanInput) {
      this.#setupSellingPlanInput();
    }
    if (targetSellingPlanId && this.#sellingPlanInput) {
      this.#sellingPlanInput.value = targetSellingPlanId.toString();
      // Only enable if subscription is selected
      if (this.#isSubscriptionSelected()) {
        this.#sellingPlanInput.disabled = false;
      }
    }

    // Update data attribute on subscription option and toggle feature visibility
    const subscriptionOption = this.querySelector(
      ".selling-plan-frequency__option--subscription"
    );
    if (subscriptionOption) {
      subscriptionOption.setAttribute(
        "data-selected-selling-plan-name",
        sellingPlanName || ""
      );

      // Update feature visibility based on matching selling plan names
      const features = this.querySelectorAll(".selling-plan-feature");
      features.forEach((feature) => {
        const featureElement = /** @type {HTMLElement} */ (feature);
        const featurePlanName = featureElement.dataset.sellingPlanName;
        if (sellingPlanName && featurePlanName === sellingPlanName) {
          featureElement.classList.add("selling-plan-feature--visible");
        } else {
          featureElement.classList.remove("selling-plan-feature--visible");
        }
      });
    }
  }

  /**
   * Selects the first available selling plan from the first variant
   */
  #selectFirstAvailableSellingPlan() {
    if (this.#sellingPlanGroups.length === 0) {
      this.#loadSellingPlanGroups();
    }

    // Find the first selling plan from the first group
    let firstSellingPlanName = null;
    for (const group of this.#sellingPlanGroups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        firstSellingPlanName = group.selling_plans[0].name;
        break;
      }
    }

    if (firstSellingPlanName) {
      this.#updateSellingPlan(firstSellingPlanName);
    }
  }

  /**
   * Loads current variant data from the product form
   */
  #loadCurrentVariant() {
    const productForm = /** @type {any} */ (
      document.querySelector("product-form-component")
    );
    if (productForm?.refs?.variantPicker) {
      const variantPicker = productForm.refs.variantPicker;
      const variantData = variantPicker?.getVariant?.();
      if (variantData) {
        this.#currentVariant = variantData;
      }
    }

    // Fallback: try to get from JSON script tag
    if (!this.#currentVariant) {
      const section = document.querySelector(
        `[data-section-id="${this.dataset.sectionId}"]`
      );
      if (section) {
        const variantScript = section.querySelector(
          'script[type="application/json"]'
        );
        if (variantScript) {
          try {
            this.#currentVariant = JSON.parse(
              variantScript.textContent || "{}"
            );
          } catch (e) {
            console.warn("Failed to parse variant data:", e);
          }
        }
      }
    }
  }

  /**
   * Gets the current quantity from quantity picker
   */
  #getCurrentQuantity() {
    const quantityPicker = document.querySelector("quantity-picker");
    if (quantityPicker) {
      const selectedRadio = /** @type {HTMLInputElement | null} */ (
        quantityPicker.querySelector('input[type="radio"]:checked')
      );
      if (selectedRadio) {
        return (
          parseInt(selectedRadio.dataset.quantity || selectedRadio.value) || 1
        );
      }
    }

    // Fallback to quantity selector
    const productForm = /** @type {any} */ (
      document.querySelector("product-form-component")
    );
    const quantitySelector = productForm?.refs?.quantitySelector;
    if (quantitySelector) {
      return parseInt(quantitySelector.getValue?.() || "1");
    }

    return 1;
  }

  /**
   * Gets the quantity block discount from the selected quantity option
   */
  #getQuantityDiscount() {
    const quantityPicker = document.querySelector("quantity-picker");
    if (quantityPicker) {
      const selectedRadio = /** @type {HTMLInputElement | null} */ (
        quantityPicker.querySelector('input[type="radio"]:checked')
      );
      if (selectedRadio) {
        const label = selectedRadio.closest("label");
        if (label) {
          const discountAmount = label.dataset.discountAmount;
          const discountType = label.dataset.discountType;
          if (discountAmount) {
            return {
              amount: parseFloat(discountAmount),
              type: discountType || "percentage",
            };
          }
        }
      }
    }
    return null;
  }

  /**
   * Formats a price in cents to a money string (removes trailing zeros only for exact dollar amounts)
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  #formatPrice(priceInCents, round = true) {
    const dollars = priceInCents / 100;
    if (priceInCents % 100 === 0) {
      return `$${Math.floor(dollars)}`;
    }
    return round ? `$${Math.round(dollars)}` : `$${dollars.toFixed(2)}`;
  }

  /**
   * Updates cost-per-day display for all quantity picker options
   */
  #updateCostPerDay() {
    const costPerDayElements = document.querySelectorAll("[data-cost-per-day]");
    const isSubscription = this.#isSubscriptionSelected();

    costPerDayElements.forEach((element) => {
      const el = /** @type {HTMLElement} */ (element);
      const baseCostPerDay = parseInt(el.dataset.baseCostPerDay || "0");
      const variantPrice = parseInt(el.dataset.variantPrice || "0");
      const quantity = parseInt(el.dataset.quantity || "1");
      const servings = parseInt(el.dataset.servings || "30");
      const discountAmount = parseFloat(el.dataset.discountAmount || "0");
      const discountType = el.dataset.discountType || "percentage";

      if (baseCostPerDay === 0 || servings === 0) return;

      let finalCostPerDay = baseCostPerDay;

      if (isSubscription) {
        // Get subscription discount from selling plan
        const subscriptionOption = /** @type {HTMLElement | null} */ (
          this.querySelector(".selling-plan-frequency__option--subscription")
        );
        const selectedSellingPlanName =
          subscriptionOption?.dataset.selectedSellingPlanName || "";

        if (selectedSellingPlanName && this.#sellingPlanGroups.length > 0) {
          // Calculate base total price
          let baseTotalPrice = quantity * variantPrice;

          // Apply quantity block discount
          let quantityDiscountValue = 0;
          if (discountAmount > 0) {
            if (discountType === "percentage") {
              quantityDiscountValue = Math.round(
                (baseTotalPrice * discountAmount) / 100
              );
            } else {
              quantityDiscountValue = Math.round(discountAmount * 100);
            }
          }

          const priceAfterQuantityDiscount =
            baseTotalPrice - quantityDiscountValue;

          // Find and apply selling plan discount
          let sellingPlanDiscount = 0;
          for (const group of this.#sellingPlanGroups) {
            const sellingPlan = group.selling_plans?.find(
              /** @param {any} plan */
              (plan) => plan.name === selectedSellingPlanName
            );

            if (sellingPlan && sellingPlan.price_adjustments?.length > 0) {
              const adjustment = sellingPlan.price_adjustments[0];

              if (adjustment.value_type === "percentage") {
                sellingPlanDiscount = Math.round(
                  (priceAfterQuantityDiscount * adjustment.value) / 100
                );
              } else if (adjustment.value_type === "fixed_amount") {
                sellingPlanDiscount = adjustment.value * quantity;
              }
              break;
            }
          }

          const subscriptionPrice =
            priceAfterQuantityDiscount - sellingPlanDiscount;

          finalCostPerDay = Math.round(subscriptionPrice / servings);
        }
      } else {
      }

      // Format and update the display
      el.textContent = this.#formatPrice(finalCostPerDay, false) + "/day";
    });
  }

  /**
   * Updates prices for both one-time and subscription options
   */
  #updatePrices() {
    if (!this.#currentVariant) {
      this.#loadCurrentVariant();
    }

    if (!this.#currentVariant) return;

    const quantity = this.#getCurrentQuantity();
    const variantPrice = this.#currentVariant.price || 0;
    const variantCompareAtPrice = this.#currentVariant.compare_at_price || 0;
    const quantityDiscount = this.#getQuantityDiscount();

    // Calculate base total price (quantity * variant price)
    let baseTotalPrice = quantity * variantPrice;

    // Apply quantity block discount if exists
    let quantityDiscountValue = 0;
    if (quantityDiscount) {
      if (quantityDiscount.type === "percentage") {
        quantityDiscountValue = Math.round(
          (baseTotalPrice * quantityDiscount.amount) / 100
        );
      } else {
        // Fixed discount - convert dollars to cents
        quantityDiscountValue = Math.round(quantityDiscount.amount * 100);
      }
    }

    const finalOneTimePrice = baseTotalPrice - quantityDiscountValue;
    let finalPrice = finalOneTimePrice; // Default to one-time price

    // Update one-time price
    const oneTimePriceElement = this.querySelector(
      '[data-price-type="one-time"] [data-current-price]'
    );
    if (oneTimePriceElement) {
      oneTimePriceElement.textContent = this.#formatPrice(finalOneTimePrice);
    }

    // Calculate subscription price
    const subscriptionPriceElement = /** @type {HTMLElement | null} */ (
      this.querySelector(
        '[data-price-type="subscription"] [data-current-price]'
      )
    );
    const subscriptionComparePriceElement = /** @type {HTMLElement | null} */ (
      this.querySelector(
        '[data-price-type="subscription"] [data-compare-price]'
      )
    );

    if (subscriptionPriceElement) {
      // Get the selected selling plan
      const subscriptionOption = /** @type {HTMLElement | null} */ (
        this.querySelector(".selling-plan-frequency__option--subscription")
      );
      const selectedSellingPlanName =
        subscriptionOption?.dataset.selectedSellingPlanName || "";

      // Always calculate and show subscription price with discount (even if not selected)
      // This serves as an incentive to choose subscription
      if (selectedSellingPlanName) {
        // First apply quantity block discount
        const priceAfterQuantityDiscount =
          baseTotalPrice - quantityDiscountValue;

        // Then find and apply the selling plan discount
        let subscriptionPrice = priceAfterQuantityDiscount;
        let sellingPlanDiscount = 0;

        try {
          const sellingPlanGroups = JSON.parse(
            this.dataset.sellingPlanGroups || "[]"
          );

          // Find the matching selling plan
          for (const group of sellingPlanGroups) {
            const sellingPlan = group.selling_plans?.find(
              /** @param {any} plan */
              (plan) => plan.name === selectedSellingPlanName
            );

            if (sellingPlan && sellingPlan.price_adjustments?.length > 0) {
              // Get the first price adjustment (typically the recurring one)
              const adjustment = sellingPlan.price_adjustments[0];

              if (adjustment.value_type === "percentage") {
                // Calculate percentage discount on price after quantity discount
                sellingPlanDiscount = Math.round(
                  (priceAfterQuantityDiscount * adjustment.value) / 100
                );
              } else if (adjustment.value_type === "fixed_amount") {
                // Fixed amount discount (in cents)
                sellingPlanDiscount = adjustment.value * quantity;
              } else if (adjustment.value_type === "price") {
                // New price (in cents)
                subscriptionPrice = adjustment.value * quantity;
                sellingPlanDiscount = 0;
              }
              break;
            }
          }
        } catch (error) {
          console.error("Error parsing selling plan groups:", error);
        }

        // Calculate final subscription price with selling plan discount
        if (sellingPlanDiscount > 0) {
          subscriptionPrice = priceAfterQuantityDiscount - sellingPlanDiscount;
        }

        const finalSubscriptionPrice = subscriptionPrice;
        const finalSubscriptionComparePrice = priceAfterQuantityDiscount;

        // Always show the subscription price (whether selected or not)
        subscriptionPriceElement.textContent = this.#formatPrice(
          finalSubscriptionPrice
        );

        // Update final price for add-to-cart button only if subscription is selected
        if (this.#isSubscriptionSelected()) {
          finalPrice = finalSubscriptionPrice;
        }

        // Show compare price if subscription price is less than compare price
        const compareElement = /** @type {HTMLElement | null} */ (
          subscriptionComparePriceElement
        );
        if (
          compareElement &&
          finalSubscriptionPrice < finalSubscriptionComparePrice
        ) {
          compareElement.textContent = this.#formatPrice(
            finalSubscriptionComparePrice
          );
          compareElement.style.display = "";
        } else if (compareElement) {
          compareElement.style.display = "none";
        }
      } else {
        // No selling plan selected, show regular price
        subscriptionPriceElement.textContent =
          this.#formatPrice(finalOneTimePrice);
        const compareElement = /** @type {HTMLElement | null} */ (
          subscriptionComparePriceElement
        );
        if (compareElement) {
          compareElement.style.display = "none";
        }
      }
    }

    // Update add-to-cart button text with price
    // setTimeout(() => this.#updateAddToCartButtonText(finalPrice), 500);
    this.#updateAddToCartButtonText(finalPrice);

    // Update cost-per-day displays
    this.#updateCostPerDay();
  }

  /**
   * Gets the number of months from the selected selling plan
   * @returns {number} Number of months (defaults to 1)
   */
  #getSellingPlanMonths() {
    const subscriptionOption = /** @type {HTMLElement | null} */ (
      this.querySelector(".selling-plan-frequency__option--subscription")
    );
    const selectedSellingPlanName =
      subscriptionOption?.dataset.selectedSellingPlanName || "";

    if (!selectedSellingPlanName) return 1;

    // Extract days from selling plan name (e.g., "Delivered every 60 days" -> 60)
    const daysMatch = selectedSellingPlanName.match(/(\d+)\s*days?/i);
    if (daysMatch && daysMatch[1]) {
      const days = parseInt(daysMatch[1], 10);
      // Convert days to months (30 days = 1 month)
      return Math.round(days / 30) || 1;
    }

    // Check for months directly (e.g., "Delivered every 2 months" -> 2)
    const monthsMatch = selectedSellingPlanName.match(/(\d+)\s*months?/i);
    if (monthsMatch && monthsMatch[1]) {
      return parseInt(monthsMatch[1], 10) || 1;
    }

    return 1;
  }

  /**
   * Updates the add-to-cart button text to include the price
   * @param {number} price - The final price in cents
   */
  #updateAddToCartButtonText(price) {
    // Find the associated add-to-cart button
    const addToCartButton = document.querySelector(
      `product-form-component[data-section-id="${this.dataset.sectionId}"] .add-to-cart-text__content span span`
    );
    if (!addToCartButton) return;

    const isSubscription = this.#isSubscriptionSelected();

    let displayPrice = price;
    let suffix = "";

    if (isSubscription) {
      // Divide price by number of months for monthly display
      const months = this.#getSellingPlanMonths();
      displayPrice = Math.round(price / months);
      suffix = "/mo";
    }

    const formattedPrice = this.#formatPrice(displayPrice);
    addToCartButton.textContent = `Add to cart - ${formattedPrice}${suffix}`;
  }
}

if (!customElements.get("selling-plan-frequency")) {
  customElements.define(
    "selling-plan-frequency",
    SellingPlanFrequencyComponent
  );
}
